import { useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { pipeCanaryCheck } from '@/security/canary';

/**
 * Resolve the API base URL from VITE_API_URL.
 *
 * Production uses `/api` (relative) — Firebase Hosting rewrites `/api/**`
 * to the `aerial-backend` Cloud Run service, so all requests are
 * SAME-ORIGIN. No CORS preflights, no cache poisoning, no issues.
 *
 * Local dev uses `http://localhost:8080/api`.
 * Fallback: the direct Cloud Run URL (cross-origin, CORS-dependent).
 */
function resolveBase(): string {
  const b = import.meta.env.VITE_API_URL || 'https://aerial-backend-415603271201.us-west1.run.app/api';
  // Relative path (e.g. "/api") → same-origin, use as-is
  if (b.startsWith('/')) return b;
  // Absolute http(s) URL → use as-is
  if (b.startsWith('http://') || b.startsWith('https://')) return b;
  // Bare hostname → add https
  return `https://${b}`;
}

// Hook form, for React components.
// Returns a STABLE memoized fetch function so callers can safely include it
// in useEffect dependency arrays without triggering infinite re-runs.
export function useApi() {
  const { getIdToken } = useAuth();

  // Always call the latest getIdToken without making it a useCallback dep.
  const getIdTokenRef = useRef(getIdToken);
  getIdTokenRef.current = getIdToken;

  // base URL never changes after mount — compute once
  const base = useMemo(() => resolveBase(), []);

  return useCallback(async (path: string, init: RequestInit = {}) => {
    const t = await getIdTokenRef.current();
    const response = await fetch(`${base}${path}`, {
      ...init,
      headers: {
        ...(init.headers || {}),
        // Send token in BOTH headers. Firebase Hosting's Cloud Run proxy
        // consumes the Authorization header for IAM auth — the app never
        // sees it. X-Firebase-Auth survives the proxy and the backend
        // checks it as a fallback.
        ...(t ? { Authorization: `Bearer ${t}`, 'X-Firebase-Auth': `Bearer ${t}` } : {}),
      },
    });
    // SEC.3: passive canary detection. If the response body contains the
    // honeypot marker, fire fingerprint capture + modal. Original response
    // is returned unchanged so call sites are unaffected.
    return pipeCanaryCheck(response);
  }, [base]); // stable: base never changes, token is read via ref
}

// ── Module-level token cache for apiFetchRaw ────────────────────────────────
//
// `apiFetchRaw` is used outside React (in DeckMapView effects, command
// helpers, etc.) where the `useApi()` hook can't run. To keep auth working
// for those paths without forcing a refactor of every call site, AuthProvider
// publishes the latest Firebase ID-token getter to this module-level ref at
// startup (`setApiFetchTokenGetter`). `apiFetchRaw` then attaches the token
// in the same dual-header pattern as `useApi`.
//
// Pre-fix (post-MA.18 polish pack 2026-06-10): `apiFetchRaw` skipped tokens
// entirely. Every authenticated endpoint it hit returned 401 silently. The
// provider choropleth + the MA.18 T.3 row-click tract endpoint were both
// affected. Operator surfaced the T.3 401 on prod.
let _apiFetchTokenGetter: (() => Promise<string | null>) | null = null;

export function setApiFetchTokenGetter(getter: (() => Promise<string | null>) | null): void {
  _apiFetchTokenGetter = getter;
}

// Non-hook util (if you need it outside React).
// Attaches the Firebase ID token in the same dual-header pattern as `useApi`
// when AuthProvider has published a token getter via `setApiFetchTokenGetter`.
// Falls back to an unauthenticated fetch when no getter is registered (e.g.,
// pre-auth-init bootstrap calls).
export async function apiFetchRaw(path: string, init: RequestInit = {}) {
  const base = resolveBase();
  let token: string | null = null;
  if (_apiFetchTokenGetter) {
    try {
      token = await _apiFetchTokenGetter();
    } catch {
      token = null;
    }
  }
  const response = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      ...(init.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}`, 'X-Firebase-Auth': `Bearer ${token}` } : {}),
    },
  });
  // SEC.3: passive canary detection (see useApi above).
  return pipeCanaryCheck(response);
}
